// ────────────────────────────────────────────────────────────────────────────
// Turning a stored one-line address into a printable block.
//
// Addresses are stored as a single Google-formatted string — "Hebron
// Industrial Estate, Kilkenny, R95 XV21, Ireland" — and the invoice snapshots
// that string verbatim. There is no structured line 1 / line 2 / city to lay
// out, so the document has to *decide* how to break it.
//
// That decision cannot be a per-template text override the way the
// organisation block is: the customer differs on every invoice, so hardcoding
// one customer's address into the layout would print it on all of them. The
// lever here is a format rule applied to whatever address the document
// carries.
//
// Splitting on every comma is the obvious move and the wrong default: a
// four-part address becomes a four-line tower, and a Dublin address with a
// unit, park, road, district, city, Eircode and country becomes eight. So the
// default matches what the invoice PDF has always done — one line that wraps.
// ────────────────────────────────────────────────────────────────────────────
import type { AddressFormat } from './types';

/** Fallback when a layout predates the format option. Matches the old PDF. */
export const DEFAULT_ADDRESS_FORMAT: AddressFormat = 'oneLine';

/** Character budget per line for `wrapped`, if a layout does not set one. */
export const DEFAULT_ADDRESS_WRAP_CHARS = 38;

/** Split on commas / newlines, trimmed, blanks dropped. */
function parts(address: string): string[] {
  return address
    .split(/\r?\n|,/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Pack comma parts into lines up to `maxChars`, never splitting a part.
 *
 * Greedy rather than balanced: an address reads top-down, so filling each line
 * before moving on keeps the parts in their natural order and puts any short
 * remainder at the bottom, which is where a country or postcode belongs
 * anyway.
 */
function wrap(address: string, maxChars: number): string[] {
  const lines: string[] = [];
  let current = '';

  for (const part of parts(address)) {
    if (!current) {
      current = part;
      continue;
    }
    const candidate = `${current}, ${part}`;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      lines.push(current);
      current = part;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * The printed lines for a stored address.
 *
 * `oneLine` hands back the whole string as a single line — react-pdf wraps it
 * inside the block's width, which is what the invoice has always done.
 */
export function formatAddressBlock(
  address: string | null | undefined,
  format: AddressFormat = DEFAULT_ADDRESS_FORMAT,
  wrapChars: number = DEFAULT_ADDRESS_WRAP_CHARS,
): string[] {
  if (!address || !address.trim()) return [];
  switch (format) {
    case 'oneLine':
      return [address.trim()];
    case 'perComma':
      return parts(address);
    case 'wrapped':
      return wrap(address, Math.max(12, wrapChars));
  }
}
